// Copyright (c) 2024-2026 AlphaGrade. All Rights Reserved.
//
// Public privacy policy. Rendered by LoginGate for /privacy regardless of
// auth so it's reachable by logged-out visitors and carrier vetting (the SMS
// section below is required for A2P 10DLC approval).

import React from 'react';

const wrap = {
  maxWidth: 760,
  margin: '0 auto',
  padding: '2.5rem 1.25rem 4rem',
  color: '#cbd5e1',
  fontFamily: '-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
  lineHeight: 1.65,
  fontSize: '0.98rem',
};
const h1 = { color: '#fff', fontSize: '1.9rem', margin: '0 0 0.25rem' };
const h2 = { color: '#fff', fontSize: '1.15rem', margin: '2rem 0 0.5rem' };
const muted = { color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 1.5rem' };
const link = { color: '#00D4AA', textDecoration: 'none' };

export default function PrivacyPolicy() {
  return (
    <div style={{ background: '#0E1525', minHeight: '100vh' }}>
      <div style={wrap}>
        <a href="/" style={{ ...link, fontSize: '0.85rem' }}>&larr; Back to AlphaGrade</a>
        <h1 style={h1}>Privacy Policy</h1>
        <p style={muted}>Last updated: November 2, 2026</p>

        <p>
          This Privacy Policy explains how AlphaGrade, a service operated by
          Hendrix, LLC ("AlphaGrade," "we," "us"), collects, uses, and protects
          information when you use our website and application at alphagrade.io
          (the "Service"). AlphaGrade is a research and analytics tool for
          investors who manage their own money.
        </p>

        <h2 style={h2}>Information We Collect</h2>
        <ul>
          <li><strong>Account information</strong> — your email address and a securely hashed password.</li>
          <li><strong>Portfolio data you enter</strong> — tickers, share counts, and entry prices you choose to save. We never ask for or store brokerage credentials.</li>
          <li><strong>Watchlists and saved analysis</strong> — the tickers and lists you choose to save, and the results of analyses you run.</li>
          <li><strong>Alpha conversations</strong> — your chats with our AI assistant, and any files you attach to them, so it has context across sessions (see the AI section below).</li>
          <li><strong>Mobile phone number</strong> — only if you opt in to SMS alerts (see the SMS section below).</li>
          <li><strong>Billing information</strong> — handled by our payment processor, Stripe. We do not store your full card details.</li>
          <li><strong>Usage data</strong> — basic technical data needed to operate and secure the Service.</li>
        </ul>

        <h2 style={h2}>SMS / Text Message Program</h2>
        <p>
          <strong>Text alerts are not currently available.</strong> The SMS program
          described below is paused, and no text messages are being sent. Alerts
          still arrive by email. The terms below apply if and when the program
          resumes.
        </p>
        <p>
          If you opt in, AlphaGrade sends transactional alert notifications about
          your saved portfolio (for example, when one of your holdings shifts its
          relationship with the broader market). You opt in inside your account
          settings by enabling alerts and verifying your mobile number with a
          one-time code. Consent is recorded at that time.
        </p>
        <ul>
          <li>Message frequency varies based on your portfolio activity.</li>
          <li>Message and data rates may apply.</li>
          <li>Reply <strong>STOP</strong> to any message to unsubscribe at any time, or <strong>HELP</strong> for help. You can also disable alerts in your account settings.</li>
        </ul>
        <p>
          <strong>
            No mobile information (including your phone number and SMS opt-in
            data) will be sold, rented, or shared with third parties or affiliates
            for marketing or promotional purposes.
          </strong>{' '}
          SMS opt-in and consent data are never shared with any third party.
        </p>

        <h2 style={h2}>How We Use Information</h2>
        <p>
          We use your information to operate the Service: to authenticate your
          account, score and analyze the portfolio you enter, deliver the
          notifications you request, process subscriptions, and keep the Service
          secure. We do not sell your personal information.
        </p>

        <h2 style={h2}>AI Features</h2>
        <p>
          Alpha, our in-app research assistant, is powered by a third-party AI
          provider. When you chat with Alpha, your messages are sent to that
          provider to generate a response. If you ask Alpha a question about your
          portfolio, the positions it needs are sent along with it.
        </p>
        <p>
          AI is also used elsewhere in the Service. In particular, if you write
          notes on a position, those notes are sent to the same provider when we
          generate research commentary for that holding. If you would rather a
          note never left our systems, leave it out.
        </p>
        <p>
          <strong>What is not sent: who you are.</strong> Your name, email address
          and account identifier are never included. The AI provider receives
          holdings without an identity attached, and cannot link them to you. Our
          provider's API terms state that data submitted through the API is not
          used to train their models.
        </p>
        <p>
          Your Alpha conversation history is stored so the assistant has context
          across sessions. It is deleted with your account.
        </p>

        <h2 style={h2}>How We Share Information</h2>
        <p>
          We share information only with service providers who help us run the
          Service under contract — for example, our payment processor (Stripe),
          our SMS provider (used solely to deliver alerts you requested), email
          delivery, hosting, and the market-data vendors that power scoring. These
          providers may only use the information to perform services for us. As
          stated above, mobile opt-in data is never shared for marketing.
        </p>

        <h2 style={h2}>Data Security &amp; Retention</h2>
        <p>
          Rather than simply promising that your data is safe, here is
          specifically what we do:
        </p>
        <ul>
          <li>
            <strong>We never hold the keys to your money.</strong> AlphaGrade does
            not ask for, receive, or store brokerage credentials, and it cannot
            place trades or move funds. A portfolio here is a list you typed or
            uploaded, nothing more.
          </li>
          <li>
            <strong>Passwords are hashed, not stored.</strong> We keep a bcrypt
            hash with a unique salt per password. We cannot read your password,
            and neither could anyone who obtained our database.
          </li>
          <li>
            <strong>Everything is encrypted in transit</strong> between your
            browser and our servers.
          </li>
          <li>
            <strong>Every request is scoped to your account.</strong> Portfolio
            data is only ever queried by the account it belongs to. There is no
            feature, screen, or administrative page anywhere in the Service that
            displays one user's holdings to another user.
          </li>
          <li>
            <strong>Your holdings are never written to our logs.</strong>
          </li>
        </ul>
        <p>
          <strong>What we do not claim.</strong> Your portfolio is stored in a
          form our systems can read, because the features you are paying for
          (scoring, alerts, and Alpha's analysis) require the Service to be able
          to read it. That means our own operations staff can technically access
          the database. We access it only to operate, support, and secure the
          Service, and we do not review individual users' holdings. We would
          rather tell you this plainly than imply a protection that does not
          exist.
        </p>
        <p>
          We retain your information only as long as needed to provide the Service
          or as required by law. No method of transmission or storage is 100%
          secure.
        </p>

        <h2 style={h2}>Your Choices</h2>
        <p>
          You can update your account information, opt out of SMS alerts (reply
          STOP or disable in settings), or cancel your subscription through
          Stripe's customer portal.
        </p>
        <p>
          <strong>Deleting your account.</strong> You can permanently delete your
          account and all associated data at any time from{' '}
          <strong>Account Settings &rarr; Delete Account</strong>. Deletion is
          immediate and irreversible: it erases your saved portfolios, watchlists,
          analysis history, Alpha conversations and stored context, and account
          record, and automatically cancels any
          active subscription. If you'd prefer, you can also request deletion by
          emailing us.
        </p>

        <h2 style={h2}>Children</h2>
        <p>The Service is intended for adults 18 and older and is not directed to children.</p>

        <h2 style={h2}>Changes to This Policy</h2>
        <p>We may update this policy from time to time; the "Last updated" date above reflects the latest version.</p>

        <h2 style={h2}>Contact</h2>
        <p>
          Questions? Email <a href="mailto:support@alphagrade.io" style={link}>support@alphagrade.io</a>.
        </p>
      </div>
    </div>
  );
}
